import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ImageUploadResult {
  file: File;
  fileName: string; // full name: "photo.jpg"
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called when user confirms — parent is responsible for uploading */
  onComplete: (result: ImageUploadResult) => void;
  /** Crop aspect ratio, undefined = free */
  aspect?: number;
  withCamera?: boolean;
  defaultMode?: 'optimize' | 'crop' | 'original';
  defaultQuality?: number;
  defaultMaxWidth?: number;
  /** Extra extensions to accept as passthrough (e.g. ['.eml', '.msg']) */
  extraExtensions?: string[];
  /** Extra accept string appended to the file input (e.g. '.eml,.msg') */
  extraAcceptTypes?: string;
}

type Step = 'source' | 'editor' | 'camera';
type Mode = 'optimize' | 'crop' | 'original';
type Area = { x: number; y: number; width: number; height: number };

// ── Canvas helpers ─────────────────────────────────────────────────────────────

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });
}

async function optimizeImg(src: string, maxW: number, qual: number, fileName: string): Promise<File> {
  const img = await createImage(src);
  const ratio = Math.min(1, maxW / img.width);
  const outW = Math.round(img.width * ratio);
  const outH = Math.round(img.height * ratio);
  const canvas = document.createElement('canvas');
  canvas.width = outW; canvas.height = outH;
  canvas.getContext('2d')!.drawImage(img, 0, 0, outW, outH);
  return new Promise(resolve =>
    canvas.toBlob(blob => resolve(new File([blob!], fileName, { type: 'image/jpeg' })), 'image/jpeg', qual),
  );
}

async function cropImg(
  src: string, px: Area, rotation: number, maxW: number, qual: number, fileName: string,
): Promise<File> {
  const img = await createImage(src);
  const maxSize = Math.max(img.width, img.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  const rotCanvas = document.createElement('canvas');
  rotCanvas.width = safeArea; rotCanvas.height = safeArea;
  const rCtx = rotCanvas.getContext('2d')!;
  rCtx.translate(safeArea / 2, safeArea / 2);
  rCtx.rotate((rotation * Math.PI) / 180);
  rCtx.translate(-img.width / 2, -img.height / 2);
  rCtx.drawImage(img, 0, 0);

  const ox = px.x + safeArea / 2 - img.width / 2;
  const oy = px.y + safeArea / 2 - img.height / 2;
  const data = rCtx.getImageData(ox, oy, px.width, px.height);

  const ratio = Math.min(1, maxW / px.width);
  const outW = Math.round(px.width * ratio);
  const outH = Math.round(px.height * ratio);

  const tmp = document.createElement('canvas');
  tmp.width = px.width; tmp.height = px.height;
  tmp.getContext('2d')!.putImageData(data, 0, 0);

  const out = document.createElement('canvas');
  out.width = outW; out.height = outH;
  out.getContext('2d')!.drawImage(tmp, 0, 0, outW, outH);

  return new Promise(resolve =>
    out.toBlob(blob => resolve(new File([blob!], fileName, { type: 'image/jpeg' })), 'image/jpeg', qual),
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Component ──────────────────────────────────────────────────────────────────

const MAX_WIDTHS = [400, 800, 1200] as const;
const ASPECTS = [
  { label: 'ImgUpFree', val: undefined },
  { label: '1:1',       val: 1 },
  { label: '16:9',      val: 16 / 9 },
  { label: '4:3',       val: 4 / 3 },
] as const;

const ImageUpload: React.FC<Props> = ({
  open,
  onClose,
  onComplete,
  aspect: propAspect,
  withCamera = true,
  defaultMode = 'optimize',
  defaultQuality = 0.85,
  defaultMaxWidth = 1200,
  extraExtensions,
  extraAcceptTypes,
}) => {
  const { t } = useTranslation();

  const [step, setStep]       = useState<Step>('source');
  const [srcTab, setSrcTab]   = useState<'file' | 'camera'>('file');

  // File info
  const [origFile, setOrigFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileExt,  setFileExt]  = useState('');

  // Loaded image (for size estimation)
  const [loadedImg, setLoadedImg] = useState<HTMLImageElement | null>(null);

  // Editor controls
  const [mode,     setMode]    = useState<Mode>(defaultMode);
  const [crop,     setCrop]    = useState({ x: 0, y: 0 });
  const [zoom,     setZoom]    = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedPx, setCroppedPx] = useState<Area | null>(null);
  const [aspIdx,   setAspIdx]  = useState(0); // index into ASPECTS
  const [quality,  setQuality] = useState(defaultQuality);
  const [maxWidth, setMaxWidth] = useState(defaultMaxWidth);

  // Camera
  const [camError, setCamError] = useState('');
  const videoRef  = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Processing
  const [processing, setProcessing] = useState(false);

  // File input
  const fileRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // ── Reset on open ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setStep('source');
    setSrcTab('file');
    setOrigFile(null);
    setImageSrc('');
    setFileName('');
    setFileExt('');
    setLoadedImg(null);
    setMode(defaultMode);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedPx(null);
    setAspIdx(propAspect != null ? 0 : 0);
    setQuality(defaultQuality);
    setMaxWidth(defaultMaxWidth);
    setCamError('');
    setProcessing(false);
  }, [open]); // eslint-disable-line

  // ── Camera stream ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!(step === 'source' && srcTab === 'camera')) return;
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } } })
      .then(stream => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
      })
      .catch(() => { if (!cancelled) setCamError(t('ImgUpCamError')); });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    };
  }, [step, srcTab, t]);

  // Load image for size estimation
  useEffect(() => {
    if (!imageSrc) { setLoadedImg(null); return; }
    const img = new Image();
    img.onload = () => setLoadedImg(img);
    img.src = imageSrc;
  }, [imageSrc]);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (imageSrc.startsWith('blob:')) URL.revokeObjectURL(imageSrc);
    onClose();
  }, [imageSrc, onClose]);

  const acceptFile = (file: File) => {
    // PDFs and other documents can't be cropped — pass them straight through.
    if (!file.type.startsWith('image/')) {
      if (file.type === 'application/pdf') {
        onComplete({ file, fileName: file.name });
        handleClose();
        return;
      }
      if (extraExtensions?.length) {
        const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
        if (extraExtensions.includes(ext)) {
          onComplete({ file, fileName: file.name });
          handleClose();
          return;
        }
      }
      return;
    }
    const dot = file.name.lastIndexOf('.');
    setFileName(dot > 0 ? file.name.slice(0, dot) : file.name);
    setFileExt(dot > 0 ? file.name.slice(dot + 1).toLowerCase() : 'jpg');
    setOrigFile(file);
    if (imageSrc.startsWith('blob:')) URL.revokeObjectURL(imageSrc);
    setImageSrc(URL.createObjectURL(file));
    setStep('editor');
  };

  const snapPhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
    setImageSrc(dataUrl);
    setFileName(`photo_${Date.now()}`);
    setFileExt('jpg');
    setOrigFile(null);
    setStep('editor');
  };

  const handleInsert = async () => {
    if (!imageSrc) return;
    setProcessing(true);
    try {
      const fullName = `${fileName}.${fileExt}`;
      let result: File;

      if (mode === 'original' && origFile) {
        result = origFile.name !== fullName
          ? new File([origFile], fullName, { type: origFile.type })
          : origFile;
      } else if (mode === 'crop' && croppedPx) {
        result = await cropImg(imageSrc, croppedPx, rotation, maxWidth, quality, fullName);
      } else {
        result = await optimizeImg(imageSrc, maxWidth, quality, fullName);
      }

      onComplete({ file: result, fileName: fullName });
      handleClose();
    } catch { /* handled by parent */ }
    setProcessing(false);
  };

  const onCropComplete = useCallback((_: Area, pixels: Area) => setCroppedPx(pixels), []);

  // ── Real size estimation — runs actual compression on debounce ────────────────
  const [estBytes, setEstBytes] = useState<number | null>(null);

  useEffect(() => {
    if (!imageSrc || !loadedImg || mode === 'original') {
      setEstBytes(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        let result: File;
        if (mode === 'crop' && croppedPx) {
          result = await cropImg(imageSrc, croppedPx, rotation, maxWidth, quality, 'p.jpg');
        } else {
          result = await optimizeImg(imageSrc, maxWidth, quality, 'p.jpg');
        }
        setEstBytes(result.size);
      } catch { setEstBytes(null); }
    }, 400);
    return () => clearTimeout(timer);
  }, [imageSrc, loadedImg, mode, maxWidth, quality, croppedPx, rotation]);

  const origBytes = origFile?.size ?? 0;
  const estKb     = estBytes != null ? Math.round(estBytes / 1024) : null;
  const savings   = estBytes != null && origBytes > 0
    ? Math.max(0, Math.round((1 - estBytes / origBytes) * 100))
    : null;

  const currentAspect = propAspect ?? ASPECTS[aspIdx].val;

  if (!open) return null;

  // ── Shared styles ─────────────────────────────────────────────────────────────

  const S = {
    overlay: {
      position: 'fixed' as const, inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    } as React.CSSProperties,
    modal: {
      background: 'var(--bg-form, #fff)',
      borderRadius: 20, overflow: 'hidden',
      width: 600, maxWidth: '100%',
      boxShadow: '0 24px 70px rgba(0,0,0,.28)',
      display: 'flex', flexDirection: 'column' as const,
      maxHeight: 'calc(100svh - 32px)',
    } as React.CSSProperties,
    head: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 20px', borderBottom: '1px solid var(--border-input)',
      flexShrink: 0,
    } as React.CSSProperties,
    foot: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '11px 20px', borderTop: '1px solid var(--border-input)',
      background: 'var(--btn-user-form)', flexShrink: 0,
    } as React.CSSProperties,
    btnGhost: {
      padding: '7px 15px', borderRadius: 9, fontSize: 12, fontWeight: 700,
      cursor: 'pointer', background: 'var(--btn-user-form)',
      color: 'var(--text-color)', border: '1px solid var(--border-input)',
    } as React.CSSProperties,
    btnPrimary: {
      padding: '7px 17px', borderRadius: 9, fontSize: 12, fontWeight: 700,
      cursor: 'pointer', border: 'none',
      background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white',
      boxShadow: '0 3px 12px rgba(37,99,235,.28)',
    } as React.CSSProperties,
    cl: {
      fontSize: 9, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' as const,
      color: 'var(--text-color-calendar-week)',
      display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6,
    } as React.CSSProperties,
    slTrack: { height: 4, borderRadius: 2, background: 'var(--border-input)', position: 'relative' as const } as React.CSSProperties,
  };

  // ── Filename row (used in editor header) ──────────────────────────────────────
  const FilenameRow = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, minWidth: 0 }}>
      <input
        title={t('ImgUpFilename')}
        value={fileName}
        onChange={e => setFileName(e.target.value)}
        style={{
          fontSize: 11, color: 'var(--text-color-calendar)',
          background: 'transparent', border: 'none',
          borderBottom: '1px dashed var(--border-input)',
          outline: 'none', padding: '0 2px',
          minWidth: 0, flex: '1 1 0', maxWidth: 180, fontFamily: 'inherit',
        }}
      />
      {fileExt && <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', fontWeight: 600, flexShrink: 0 }}>.{fileExt}</span>}
      {loadedImg && (
        <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 4 }}>
          · {formatBytes(origFile?.size ?? 0)} · {loadedImg.width}×{loadedImg.height}
        </span>
      )}
    </div>
  );

  // ── MODAL HEADER ──────────────────────────────────────────────────────────────
  const ModalHead = ({ icon, title, sub }: { icon: string; title: string; sub?: React.ReactNode }) => (
    <div style={S.head}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, background: 'rgba(37,99,235,.1)', flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-color)', letterSpacing: '-.01em' }}>{title}</div>
          {sub && <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>{sub}</div>}
        </div>
      </div>
      {!processing && (
        <button type="button" onClick={handleClose} style={{
          width: 26, height: 26, borderRadius: 7, background: 'var(--btn-user-form)',
          border: '1px solid var(--border-input)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', cursor: 'pointer', fontSize: 12,
          color: 'var(--text-color-calendar-week)', flexShrink: 0, marginLeft: 8,
        }}>✕</button>
      )}
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════════
  // STEP: SOURCE
  // ══════════════════════════════════════════════════════════════════════════════

  if (step === 'source') return createPortal(
    <div data-portal-modal style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div style={S.modal}>
        <ModalHead icon="🖼️" title={t('ImgUpTitle')} sub={t('ImgUpTitleSub')} />

        {/* Source tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-input)', padding: '0 20px', flexShrink: 0 }}>
          {(['file', 'camera'] as const).filter(s => s === 'file' || withCamera).map(s => (
            <button key={s} type="button"
              onClick={() => setSrcTab(s)}
              style={{
                padding: '10px 16px', fontSize: 12, fontWeight: 700,
                border: 'none', background: 'transparent',
                color: srcTab === s ? '#2563eb' : 'var(--text-color-calendar-week)',
                cursor: 'pointer', borderBottom: `2px solid ${srcTab === s ? '#2563eb' : 'transparent'}`,
                marginBottom: -1, fontFamily: 'inherit',
              }}>
              {s === 'file' ? `📁 ${t('ImgUpFile')}` : `📷 ${t('ImgUpCamera')}`}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {srcTab === 'file' ? (
            /* Dropzone */
            <div
              style={{ margin: '14px 20px 16px', border: '2px dashed var(--border-input)', borderRadius: 13, padding: '28px 20px', textAlign: 'center', cursor: 'pointer', background: 'var(--btn-user-form)' }}
              onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) acceptFile(f); }}
            >
              <div style={{ width: 46, height: 46, borderRadius: 13, background: 'linear-gradient(135deg,rgba(37,99,235,.1),rgba(124,58,237,.1))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, margin: '0 auto 10px' }}>🖼️</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)', marginBottom: 4 }}>{t('ImgUpDrop')}</div>
              <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{t('ImgUpDropSub')}</div>
              <button type="button"
                style={{ marginTop: 10, padding: '6px 16px', borderRadius: 9, fontSize: 11, fontWeight: 700, background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white', cursor: 'pointer', border: 'none', boxShadow: '0 3px 12px rgba(37,99,235,.28)' }}>
                {t('ImgUpChooseFile')}
              </button>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
                {[...['JPG', 'PNG', 'WEBP', 'GIF', 'SVG', 'PDF'], ...(extraExtensions?.map(e => e.slice(1).toUpperCase()) ?? [])].map(f => (
                  <span key={f} style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: 'var(--bg-form)', color: 'var(--text-color-calendar-week)', border: '1px solid var(--border-input)' }}>{f}</span>
                ))}
              </div>
            </div>
          ) : (
            /* Camera view */
            <>
              <div style={{ position: 'relative', background: '#04060f', minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', maxHeight: 260, objectFit: 'cover', display: 'block' }} />
                <div style={{ position: 'absolute', top: 10, left: 12, padding: '3px 8px', borderRadius: 6, background: 'rgba(239,68,68,.88)', color: 'white', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'white', animation: 'iuc-pulse 1s infinite', display: 'inline-block' }} />
                  LIVE
                </div>
                {camError && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, textAlign: 'center', color: 'rgba(255,255,255,.7)', fontSize: 13 }}>
                    ⚠️ {camError}
                  </div>
                )}
              </div>
              <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button type="button" onClick={handleClose} style={S.btnGhost}>{t('Cancel')}</button>
                <button type="button" onClick={snapPhoto} disabled={!!camError}
                  style={{ ...S.btnPrimary, display: 'flex', alignItems: 'center', gap: 8, opacity: camError ? .45 : 1, cursor: camError ? 'not-allowed' : 'pointer' }}>
                  📷 {t('ImgUpSnap')}
                </button>
                <div style={{ width: 80 }} />
              </div>
            </>
          )}
        </div>

        <div style={S.foot}>
          <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{t('ImgUpDropSub')}</div>
          <button type="button" onClick={handleClose} style={S.btnGhost}>{t('Cancel')}</button>
        </div>
      </div>

      <input ref={fileRef} type="file"
        accept={`image/*,application/pdf${extraAcceptTypes ? ',' + extraAcceptTypes : ''}`}
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) acceptFile(f); e.target.value = ''; }} />

      <style>{`
        @keyframes iuc-pulse { 0%,100%{opacity:1} 50%{opacity:.2} }
        @keyframes iuc-spin  { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  , document.body);

  // ══════════════════════════════════════════════════════════════════════════════
  // STEP: EDITOR
  // ══════════════════════════════════════════════════════════════════════════════

  const isOrig  = mode === 'original';
  const isCrop  = mode === 'crop';

  return createPortal(
    <div data-portal-modal style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget && !processing) handleClose(); }}>
      <div style={S.modal}>

        <ModalHead
          icon="✂️"
          title={t('ImgUpEditorTitle')}
          sub={<FilenameRow />}
        />

        {/* Editor body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 280 }}>

          {/* Canvas */}
          <div style={{ flex: 1, background: '#08101e', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {isCrop && imageSrc ? (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={currentAspect}
                cropShape="rect"
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            ) : (
              <>
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 70% 55% at 50% 40%, rgba(37,99,235,.05), transparent)' }} />
                {imageSrc && (
                  <img src={imageSrc} alt=""
                    style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: 6, border: '1.5px solid rgba(255,255,255,.12)', opacity: isOrig ? .65 : 1 }} />
                )}
              </>
            )}
            {!isCrop && (
              <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,.22)' }}>
                {isOrig ? t('ImgUpModeOriginalHint') : t('ImgUpModeOptimizeHint')}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div style={{ width: 170, flexShrink: 0, borderLeft: '1px solid var(--border-input)', padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 12, background: 'var(--bg-form)', overflowY: 'auto' }}>

            {/* Mode segmented control */}
            <div>
              <div style={S.cl}><span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2563eb', display: 'inline-block' }} />{t('ImgUpMode')}</div>
              <div style={{ display: 'flex', background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 9, padding: 2, gap: 2 }}>
                {(['optimize', 'crop', 'original'] as Mode[]).map(m => (
                  <button key={m} type="button"
                    onClick={() => setMode(m)}
                    style={{
                      flex: 1, padding: '5px 2px', borderRadius: 7, fontSize: 9, fontWeight: 800,
                      textAlign: 'center', cursor: 'pointer', border: 'none', lineHeight: 1.3, fontFamily: 'inherit',
                      background: mode === m ? 'var(--bg-form)' : 'transparent',
                      color: mode === m ? 'var(--text-color)' : 'var(--text-color-calendar-week)',
                      boxShadow: mode === m ? '0 1px 4px var(--border-input)' : 'none',
                    }}>
                    {m === 'optimize' ? `🗜️\n${t('ImgUpModeOptimize')}` : m === 'crop' ? `✂️\n${t('ImgUpModeCrop')}` : `📁\n${t('ImgUpModeOriginal')}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Aspect ratio — only in crop mode */}
            <div style={{ opacity: isCrop ? 1 : .28, pointerEvents: isCrop ? 'auto' : 'none' }}>
              <div style={S.cl}><span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2563eb', display: 'inline-block' }} />{t('ImgUpRatio')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                {ASPECTS.map((a, i) => (
                  <button key={i} type="button" onClick={() => setAspIdx(i)}
                    style={{
                      padding: '4px 3px', borderRadius: 6, fontSize: 9, fontWeight: 700, textAlign: 'center',
                      cursor: 'pointer', border: `1px solid ${aspIdx === i ? 'rgba(37,99,235,.3)' : 'var(--border-input)'}`,
                      background: aspIdx === i ? 'rgba(37,99,235,.1)' : 'var(--btn-user-form)',
                      color: aspIdx === i ? '#2563eb' : 'var(--text-color-calendar)', fontFamily: 'inherit',
                    }}>
                    {a.label === 'ImgUpFree' ? t('ImgUpFree') : a.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Zoom — only in crop mode */}
            <div style={{ opacity: isCrop ? 1 : .28, pointerEvents: isCrop ? 'auto' : 'none' }}>
              <div style={S.cl}><span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2563eb', display: 'inline-block' }} />{t('ImgUpZoom')}</div>
              <input type="range" min={1} max={3} step={0.05} value={zoom}
                onChange={e => setZoom(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#2563eb', cursor: 'pointer' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>
                <span>1×</span><span>{zoom.toFixed(1)}×</span><span>3×</span>
              </div>
            </div>

            {/* Rotation — only in crop mode */}
            <div style={{ opacity: isCrop ? 1 : .28, pointerEvents: isCrop ? 'auto' : 'none' }}>
              <div style={S.cl}><span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2563eb', display: 'inline-block' }} />{t('ImgUpRotation')}</div>
              <input type="range" min={-45} max={45} step={1} value={rotation}
                onChange={e => setRotation(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#2563eb', cursor: 'pointer' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>
                <span>−45°</span><span>{rotation}°</span><span>+45°</span>
              </div>
            </div>

            {/* Quality */}
            <div style={{ opacity: isOrig ? .28 : 1, pointerEvents: isOrig ? 'none' : 'auto' }}>
              <div style={{ ...S.cl, justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2563eb', display: 'inline-block' }} />
                  {t('ImgUpQuality')}
                </div>
                <span style={{ color: '#059669', fontWeight: 800 }}>{Math.round(quality * 100)}%</span>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: 'linear-gradient(90deg,#ef4444 0%,#f59e0b 28%,#10b981 62%,#10b981 100%)', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '50%', left: `${(quality - 0.5) / 0.5 * 100}%`, transform: 'translate(-50%,-50%)', width: 12, height: 12, borderRadius: '50%', background: 'white', border: '2px solid #059669', boxShadow: '0 1px 4px rgba(5,150,105,.3)', cursor: 'pointer' }} />
              </div>
              <input type="range" min={0.5} max={1} step={0.05} value={quality}
                onChange={e => setQuality(Number(e.target.value))}
                style={{ width: '100%', opacity: 0, height: 5, marginTop: -5, cursor: 'pointer', position: 'relative', zIndex: 1 }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>
                <span>50%</span><span>100%</span>
              </div>
            </div>

            {/* Max width */}
            <div style={{ opacity: isOrig ? .28 : 1, pointerEvents: isOrig ? 'none' : 'auto' }}>
              <div style={S.cl}><span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2563eb', display: 'inline-block' }} />{t('ImgUpMaxWidth')}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {MAX_WIDTHS.map(w => (
                  <button key={w} type="button" onClick={() => setMaxWidth(w)}
                    style={{
                      padding: '3px 7px', borderRadius: 20, fontSize: 9, fontWeight: 700,
                      border: `1px solid ${maxWidth === w ? 'rgba(37,99,235,.35)' : 'var(--border-input)'}`,
                      background: maxWidth === w ? 'rgba(37,99,235,.1)' : 'var(--btn-user-form)',
                      color: maxWidth === w ? '#2563eb' : 'var(--text-color-calendar)', cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                    {w}px
                  </button>
                ))}
              </div>
            </div>

            {/* Size estimate */}
            <div style={{ marginTop: 'auto', padding: '8px 9px', borderRadius: 8, background: isOrig ? 'rgba(100,116,139,.07)' : 'rgba(5,150,105,.07)', border: `1px solid ${isOrig ? 'rgba(100,116,139,.18)' : 'rgba(5,150,105,.18)'}` }}>
              {isOrig ? (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-color-calendar)' }}>{formatBytes(origBytes)}</div>
                  <div style={{ fontSize: 9, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>{t('ImgUpModeOriginal')}</div>
                </>
              ) : origBytes > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: 9, color: 'var(--text-color-calendar-week)' }}>
                    <div style={{ color: 'var(--text-color-calendar)', fontWeight: 700 }}>{formatBytes(origBytes)}</div>
                    <div>{t('ImgUpOriginalSize')}</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>→</div>
                  <div style={{ fontSize: 9, textAlign: 'right', color: 'var(--text-color-calendar-week)' }}>
                    {estKb != null ? (
                      <>
                        <div style={{ color: '#059669', fontWeight: 800 }}>{formatBytes(estBytes!)}</div>
                        {savings != null && <div>↓ {savings}%</div>}
                      </>
                    ) : (
                      <div style={{ color: 'var(--text-color-calendar-week)' }}>…</div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 9, color: 'var(--text-color-calendar-week)' }}>
                  {estKb != null ? `${t('ImgUpAfterSize')}: ${formatBytes(estBytes!)}` : '…'}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Processing overlay */}
        {processing && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', border: '3px solid rgba(255,255,255,.2)', borderTopColor: 'white', animation: 'iuc-spin .7s linear infinite' }} />
          </div>
        )}

        {/* Footer — WARNING for original, normal otherwise */}
        {isOrig ? (
          <div style={{ padding: '14px 20px', background: 'rgba(217,119,6,.07)', borderTop: '2px solid rgba(217,119,6,.22)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#d97706', marginBottom: 3 }}>{t('ImgUpOriginalWarn')}</div>
                <div style={{ fontSize: 11, color: 'var(--text-color-calendar)', lineHeight: 1.5 }}>
                  {t('ImgUpOriginalWarnText')
                    .replace('{size}', formatBytes(origBytes))
                    .replace('{opt}', estKb != null ? formatBytes(estBytes!) : '…')
                    .replace('{pct}', savings != null ? String(savings) : '…')}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button type="button" onClick={() => setMode('optimize')} style={S.btnPrimary}>
                    🗜️ {t('ImgUpCompress')}
                  </button>
                  <button type="button" onClick={handleInsert} disabled={processing}
                    style={{ ...S.btnGhost, color: 'var(--text-color-calendar-week)', borderColor: 'rgba(217,119,6,.3)' }}>
                    {t('ImgUpUploadOriginal')} →
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={S.foot}>
            <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>
              {loadedImg && !isOrig && estKb != null && (
                <>
                  {t('ImgUpAfterSize')}: <b style={{ color: 'var(--text-color-calendar)' }}>
                    JPEG · {formatBytes(estBytes!)}
                    {savings != null && origBytes > 0 && ` ↓${savings}%`}
                  </b>
                </>
              )}
              {loadedImg && !isOrig && estKb == null && (
                <span style={{ opacity: .5 }}>…</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setStep('source')} style={S.btnGhost} disabled={processing}>
                {t('ImgUpBack')}
              </button>
              <button type="button" onClick={handleInsert} disabled={processing} style={{ ...S.btnPrimary, opacity: processing ? .6 : 1, cursor: processing ? 'not-allowed' : 'pointer' }}>
                {processing
                  ? <><div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(255,255,255,.3)', borderTopColor: 'white', animation: 'iuc-spin .7s linear infinite', display: 'inline-block', marginRight: 6 }} />{t('ImgUpProcessing')}</>
                  : t('ImgUpInsert')
                }
              </button>
            </div>
          </div>
        )}

      </div>
      <style>{`
        @keyframes iuc-pulse { 0%,100%{opacity:1} 50%{opacity:.2} }
        @keyframes iuc-spin  { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  , document.body);
};

export default ImageUpload;
